import { type ReactElement, useCallback, useMemo } from 'react';

import { deepEqual, shallowEqual } from 'fast-equals';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { policyConstants, tournamentEngine } from 'tods-competition-factory';

import { tournamentAtom } from 'atoms/tournament';
import { getClientConfig } from 'config/clients';
import useTournamentPolicies from 'hooks/policies/useTournamentPolicies';
import { useTournamentContext } from 'hooks/tournament/useTournamentContext';
import { useLevelConfig } from 'hooks/useLevelConfiguration';
import useMemoCompare from 'hooks/useMemoCompare';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import {
  getEventTypeFromSubScaling,
  getParticipantScaling,
  getParticipantScalingDisplay,
  getParticipantScalingText,
  getParticipantScalingValue,
  getPtValueTypeFromSubScaling,
  getScaledEntries,
  getScaleDisplayName,
  getScaleSortMethod,
  type GetScalingOptionsParams,
  getSubScalings,
  type ScaledEntry,
  type ScalingOptions,
  type ScalingPurpose,
  type ScalingType,
  type ScalingTypeOptions,
  scalingTypeToScaling,
  seed,
  type SubScalingType,
} from 'utils/scalings';

const { POLICY_TYPE_AVOIDANCE } = policyConstants;
const emptyObj = {};

interface UseScalingsResult {
  /**
   * A list of all possible scalingTypes
   */
  scalingTypes: [ScalingType, ScalingTypeOptions?][];
  /**
   * A list of available scalingTypes
   */
  availableScalingTypes: [ScalingType, ScalingTypeOptions?][];
  /**
   *
   * @param scalingType The name of a scaling type
   * @param participants An array of participant objects
   * @param drawSize The size of the draw
   * @param drawStage The stage of the draw
   *.@param drawId Optional id of the draw
   * @param seedsCount An optional number to truncate the entries returned
   * @returns Returns a object that contains the original participant and any associated scaleItems
   */
  seed(
    scalingType: ScalingType,
    participants: ParticipantInterface[],
    drawSize: number,
    drawStage: 'MAIN' | 'QUALIFYING',
    drawId?: string,
    seedsCount?: number,
  ): any;
  /**
   *
   * @param scalingType The name of a scaling type
   * @param participants An array of participant objects
   * @param seedsCount An optional number to truncate the entries returned
   * @returns An array of ScaledEntry for the given parameters
   */
  selection(
    scalingType: ScalingType,
    participants: ParticipantInterface[],
    seedsCount?: number,
    scalingTypeOptions?: ScalingTypeOptions,
  ): ScaledEntry[];
  /**
   *
   * @param scalingType The name of a scaling type
   * @param participant A participant object
   * @param subScalingType The name of a sub scaling type
   * @returns A value that represents the actual internal value that is not normally displayed to the user
   */
  getValue(
    scalingType: ScalingType,
    participant: ParticipantInterface,
    subScalingType?: ScalingType | SubScalingType,
  ): string | number;
  /**
   *
   * @param scalingType The name of a scaling type
   * @param participant A participant object
   * @param subScalingType The name of a sub scaling type
   * @returns A plain string value that represents what the user would normally see
   */
  getDisplayValue(
    scalingType: ScalingType,
    participant: ParticipantInterface,
    subScalingType?: ScalingType | SubScalingType,
  ): string | number | ReactElement;
  /**
   *
   * @param scalingType The name of a scaling type
   * @returns An array of subScaling names or undefined
   */
  getSubScalings(scalingType: ScalingType): SubScalingType[] | undefined;
  /**
   *
   * @param scalingType The name of a scaling type
   * @param participant A participant object
   * @param subScalingType The name of a sub scaling type
   * @returns A JSX compatible value representing the value used to display in the DOM
   */
  getText(
    scalingType: ScalingType,
    participant: ParticipantInterface,
    subScalingType?: ScalingType | SubScalingType,
  ): string;
  /**
   * @param scalingType The name of a scaling type
   * @param purpose The purpose for the sort method
   * @param subScalingType The name of a sub scaling type
   * @returns A sort function for each participant
   */
  getSortMethod(
    scalingType: ScalingType,
    purpose: ScalingPurpose,
    subScalingType?: ScalingType | SubScalingType,
  ): (a: ParticipantInterface, b: ParticipantInterface) => number;
  /**
   *
   * @param scalingType The name of a scaling type
   * @param subScalingType The name of a sub scaling type
   * @returns A plain string value of the scale name to display to the user
   */
  getDisplayName(scalingType: ScalingType, subScalingType?: ScalingType | SubScalingType): string;
  /**
   *
   * @param scalingType The name of a scaling type
   * @returns Get natural sorting direction for the scaling type
   */
  getSortDirection(scalingType: ScalingType): 'normal' | 'inverted';
}

function useScalings(options: Omit<ScalingOptions, 'policyDefinitions'>): UseScalingsResult {
  const scalingTypes = useMemo(() => getClientConfig().scalingTypes, []);
  const { tournament } = useTournamentContext();
  const tournamentPolicies = useTournamentPolicies(tournament?.tournamentId);
  const levelConfig = useLevelConfig();

  const memoisedOptions = useMemoCompare(options, shallowEqual);

  const getPolicyDefinitions = useCallback(
    (tournament) =>
      tournament &&
      tournamentEngine.getPolicyDefinitions({
        eventId: memoisedOptions?.event?.eventId,
        policyTypes: [POLICY_TYPE_AVOIDANCE],
      })?.policyDefinitions,
    [memoisedOptions?.event?.eventId],
  );

  const policyDefinitions = useAtomValue(selectAtom(tournamentAtom, getPolicyDefinitions, deepEqual));

  const getOptions = useCallback(
    ({ scalingType, subScalingType, drawId }: GetScalingOptionsParams): ScalingOptions => {
      const eventType = getEventTypeFromSubScaling(subScalingType);
      const ptValueType = getPtValueTypeFromSubScaling(scalingType, subScalingType);

      return {
        ...memoisedOptions,
        event: eventType ? { ...memoisedOptions.event, eventType } : memoisedOptions.event,
        policyDefinitions: { seeding: tournamentPolicies?.seeding, ...policyDefinitions },
        drawId,
        ptValueType,
      };
    },
    [memoisedOptions, policyDefinitions, tournamentPolicies?.seeding],
  );

  const availableScalingTypes = useMemo(
    () =>
      scalingTypes.filter(([scalingType]) => {
        const scaling = scalingTypeToScaling(scalingType);
        return !!scaling?.getIsAvailable(memoisedOptions?.event, levelConfig);
      }),
    [scalingTypes, memoisedOptions?.event, levelConfig],
  );

  return useMemo(
    () => ({
      scalingTypes,
      availableScalingTypes,
      seed: (scalingType, participants, drawSize, drawStage, drawId, seedsCount = Infinity) => {
        const scalingTypeOptions = scalingTypes.find(([sT]) => sT === scalingType)?.[1];
        const scaledEntries = getScaledEntries(
          participants,
          scalingType,
          getOptions(emptyObj),
          scalingTypeOptions,
          'SEED',
          seedsCount,
        );
        return seed(scalingType, scaledEntries, drawSize, drawStage, getOptions({ scalingType, drawId }));
      },
      selection: (scalingType, participants, seedsCount = Infinity) => {
        const scalingTypeOptions = scalingTypes.find(([sT]) => sT === scalingType)?.[1];
        return getScaledEntries(
          participants,
          scalingType,
          getOptions(emptyObj),
          scalingTypeOptions,
          'SELECTION',
          seedsCount,
        );
      },
      getValue: (scalingType, participant, subScalingType) => {
        const options = getOptions({ scalingType, subScalingType });
        const participantScaling = getParticipantScaling(scalingType, participant, options);
        return getParticipantScalingValue(scalingType, participantScaling, options);
      },
      getDisplayValue: (scalingType, participant, subScalingType) => {
        const options = getOptions({ scalingType, subScalingType });
        const scalingTypeOptions = { ...scalingTypes.find(([sT]) => sT === scalingType)?.[1], ...options };
        const participantScaling = getParticipantScaling(scalingType, participant, options);
        return getParticipantScalingDisplay(scalingType, participantScaling, scalingTypeOptions);
      },
      getSubScalings: (scalingType) => {
        return getSubScalings(scalingType);
      },
      getText: (scalingType, participant, subScalingType) => {
        const options = getOptions({ scalingType, subScalingType });
        const scalingTypeOptions = { ...scalingTypes.find(([sT]) => sT === scalingType)?.[1], ...options };
        const participantScaling = getParticipantScaling(scalingType, participant, options);
        return getParticipantScalingText(scalingType, participantScaling, scalingTypeOptions);
      },
      getSortMethod: (scalingType, purpose, subScalingType) => {
        const options = getOptions({ scalingType, subScalingType });
        const scalingTypeOptions = { ...scalingTypes.find(([sT]) => sT === scalingType)?.[1], ...options };
        const scaleSortMethod = getScaleSortMethod(scalingType, purpose, scalingTypeOptions);
        return (a: ParticipantInterface, b: ParticipantInterface) => {
          const participantScalingA = getParticipantScaling(scalingType, a, options);
          const participantScalingB = getParticipantScaling(scalingType, b, options);
          return scaleSortMethod(participantScalingA, participantScalingB);
        };
      },
      getDisplayName: (scalingType, subScalingType) => {
        return getScaleDisplayName(scalingType, getOptions({ scalingType, subScalingType }));
      },
      getSortDirection: (scalingType) => {
        const scaling = scalingTypeToScaling(scalingType);
        return scaling.sortDirection;
      },
    }),
    [availableScalingTypes, getOptions, scalingTypes],
  );
}

export default useScalings;
